<template>
  <div
    :class="[
      'vgp_date_picker',
      customSelector ? 'date_range_modification' : '',
      isRange ? 'range_picker' : '',
      hasRangeDiff ? 'range_has_diff' : ''
    ]"
  >
    <date-picker
      ref="calendar"
      :key="componentValue"
      v-model="componentValue"
      mode="date"
      :model-modifiers="modifiers"
      :class="animationClass"
      :is-required="true"
      :attributes="calendarAttributes"
      :locale="calendarLocale"
      :max-date="maxDate"
      :columns="columns"
      :popover="{
        placement: 'top-end',
        showDelay: 0,
        hideDelay: 0,
        transition: 'custom-popover-transition',
      }"
      :min-date="minDate"
      @update:modelValue="onInput"
      @popoverDidShow="onShow"
      @popoverDidHide="onHide"
    >
      <template
        v-if="$slots.default"

        #default="{ inputValue, togglePopover, inputEvents }"
      >
        <slot
          v-if="$slots.default"
          :inputValue="inputValue"
          :togglePopover="togglePopover"
          :inputEvents="inputEvents"
        />
      </template>

      <template
        v-if="hasFooterSlot"
        #footer
      >
        <div :class="['vgp_date_picker_footer',showTimePicker ? 'vgp_date_picker_footer_hide_footer' : '' ]">
          <div :class="'default_footer'">
            <div
              v-if="currentTime && timePicker"
              :class="'time-picker_label'"
              @click="selectTimePicker"
            >
              <svg-sprite
                :name="'time'"
                :type="'regular'"
                :size="24"
              />
              {{ currentTime }}
            </div>
            <button
              v-if="resetButton && selectedDate"
              :class="'vgp_date_picker_footer_button'"
              @click.stop="resetDate"
            >
              {{ locales('custom_column_recet_button_text') }}
            </button>
            <slot
              v-if="$slots.footer"
              name="footer"
              :setValue="setValue"
            />

            <button
              v-else
              class="time-picker_label float-right"
              @click.stop="() => setValue(new Date())"
            >
              {{ locales('datepiker_today') }}
            </button>
          </div>
          <div
            v-if="timePicker"
            :class="[animationClassForPicker, 'timePicker_container' ]"
          >
            <vgp-time-picker
              v-if="timePicker"
              :current-date="value"
              :time-data="availableHours"
              :time-format="timeFormat"
              :back-button="true"
              :date-type="startOrEnd"
              @changeTime="handleChangeTime"
              @backToDate="backClicked"
            />
          </div>
        </div>
      </template>
    </date-picker>
  </div>
</template>

<script>
import { DatePicker } from 'v-calendar';
import svgSprite from '$$vueCmp/VueIcon/svgSprite.vue';
import vgpTimePicker from './vgpTimePicker.vue';
import calendarModule from '../../../../components/calendar';

export default {
  name: 'VgpDatePicker',
  components: { DatePicker, svgSprite, vgpTimePicker },
  props: {
    value: { type: [Date, String, Number], default: null },
    workDays: { type: Array, required: false, default: () => [1, 2, 3, 4, 5] },
    rangeValue: {
      type: Object,
      required: false,
      default: () => ({
        start: null,
        end: null,
      }),
    },
    minDate: { type: Object, required: false, default: undefined },
    maxDate: { type: Object, required: false, default: undefined },
    isRange: { type: Boolean, required: false, default: false },
    columns: { type: Number, required: false, default: 1 },
    hasFooterSlot: { type: Boolean, required: false, default: true },
    backButton: {
      type: Boolean,
      default: false,
      required: false,
    },
    resetButton: {
      type: Boolean,
      default: false,
    },
    timeData: {
      type: Object,
      default: () => {},
      required: false,
    },
    timePicker: {
      type: Boolean,
      default: false,
      required: false,
    },
    stepMinutes: {
      type: Number,
      default: 5,
      required: false,
    },
    ganttId: {
      type: String,
      default: '',
      required: false,
    },
    startOrEnd: {
      type: String,
      default: '',
      required: false,
    },
    isCustomDateColumn: {
      type: Boolean,
      default: false,
      required: false,
    },
    selectedDate: {
      type: Boolean,
      default: false,
      required: false,
    },
    isRangeComponent: { type: Boolean, required: false, default: false },
    customSelector: { type: Boolean, required: false, default: false },
    calendarConfig: {
      type: Object,
      default: null,
    },
  },
  emits: {
    onChange: null,
    onShow: null,
    onClose: null,
    onChangeTime: null,
  },
  data() {
    const calendarWorkDays = this.workDays.map(day => (day === 7 ? 1 : ++day)); // Day of week begins on Sunday in v-calendar

    return {
      locales: __,
      calendarLocale: {
        id: this.getCalendarLocale(),
        firstDayOfWeek: +user.settings.start_monday ? 2 : 1,
        masks: {
          input: user.dateFormat ? user.dateFormat : 'YYYY-MM-DD',
        },
      },
      showTimePicker: false,
      timeClicked: false,
      animationClass: '',
      animationClassForPicker: '',
      hasRangeDiff: false,
      calendarWorkDays,
      componentValue: this.isRange ? this.rangeValue : this.getClosestFutureWorkDay(this.value, calendarWorkDays),
    };
  },
  computed: {
    modifiers() {
      return {
        range: this.isRange,
      };
    },

    calendarAttributes() {
      return [
        {
          key: 'highlight',
          highlight: {
            contentStyle: { color: '#B2B2B2', borderRadius: '4px', pointerEvents: 'none' },
            style: { backgroundColor: 'unset' },
            contentClass: 'dayHighlightActive',
          },
          dates: {
            start: null,
            end: null,
            repeat: {
              on: [this.isBlockedDate],
            },
          },
        },
      ];
    },

    timeFormat() {
      return user.timeFormat;
    },

    availableHours() {
      return this.calendarConfig ? this.calendarConfig.parsed.hours : {};
    },

    currentTime() {
      if (this.timePicker) {
        let hours = this.value.getHours();
        const minutes = this.value.getMinutes().toString().padStart(2, '0');
        let ampm = 'AM';

        if (!this.timeFormat) {
          if (hours > 12) {
            hours -= 12;
            ampm = 'PM';
          }

          return `${hours < 10 ? `0${hours}` : hours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
        }

        return `${hours < 10 ? `0${hours}` : hours}:${minutes}`;
      }

      return null;
    },
  },
  watch: {
    componentValue(value) {
      if (this.isRange) {
        const startDate = new Date(value.start);
        const endDate = new Date(value.end);
        const isDifferentDay = startDate.getMonth() !== endDate.getMonth() || startDate.getDate() !== endDate.getDate();

        if (isDifferentDay) {
          this.hasRangeDiff = true;
        } else {
          this.hasRangeDiff = false;
        }
      }
    },
    rangeValue(val) {
      if (this.isRange) {
        this.componentValue = val;
      }
    },
    value(val) {
      if (!this.isRange) {
        this.componentValue = this.getClosestFutureWorkDay(val, this.calendarWorkDays);
      }
    },
  },
  methods: {
    setValue(value) {
      const closestWorkDay = this.getClosestFutureWorkDay(value, this.calendarWorkDays);

      this.$refs.calendar?.updateValue(closestWorkDay);
      this.$refs.calendar?.hidePopover();
    },

    onInput(value) {
      this.$emit('onChange', value, this.startOrEnd);
    },

    onShow() {
      this.$emit('onShow');
    },

    onHide() {
      this.$emit('onClose');
    },

    getClosestFutureWorkDay(date, workDays = []) {
      if (!date) {
        return null;
      }

      if (!workDays.length) {
        return date;
      }
      if (this.isCustomDateColumn) return date;

      let closestDay = new Date();

      if (date && date !== '') {
        closestDay = new Date(date);
      }

      let i = 0;

      while (!workDays.includes(closestDay.getDay() + 1) && i < 7) {
        closestDay.setDate(closestDay.getDate() + 1);
        i++;
      }

      return closestDay;
    },
    handleChangeTime(value) {
      this.$emit('onChangeTime', value, this.startOrEnd);
    },
    selectTimePicker() {
      this.animationClass = 'hide-date';
      this.animationClassForPicker = 'show-picker';
      this.timeClicked = false;
      this.showTimePicker = false;
    },
    backClicked() {
      this.animationClass = 'show-date';
      this.animationClassForPicker = 'hide-picker';
      this.timeClicked = false;
      this.showTimePicker = false;

      setTimeout(() => {
        this.animationClass = '';
        this.animationClassForPicker = '';
      }, 500);
    },
    move(date) {
      this.$refs.calendar.move(date);
    },
    resetDate() {
      this.$emit('onChange', '');
    },
    getCalendarLocale() {
      if (user.locale === 'kr') {
        return 'ko';
      }

      return user.locale;
    },
    isBlockedDate(days) {
      if (this.isCustomDateColumn) return;
      if (!this.calendarConfig) {
        return !this.calendarWorkDays.find(workDay => workDay === days.weekday);
      }

      if (calendarModule.isWorkDay(days.date, this.calendarConfig)) return false;

      return true;
    },
  },
};
</script>

<style src="./datePicker.less" lang="less"></style>
<style scoped lang="less">
  .custom-popover-transition-enter-active,
  .custom-popover-transition-leave-active {
    transition: opacity 0.001s;
  }

  .custom-popover-transition-enter-from,
  .custom-popover-transition-leave-to {
    opacity: 0;
  }

  .custom-popover-transition-enter-to,
  .custom-popover-transition-leave-from {
    opacity: 1;
  }
</style>
